import * as React from 'react';
/**
 * Effect that can be used to perform scroll effects. It emits events when the user
 * scrolls to the top and when a scroll has been initiated. An example usage would be
 * to show a drop shadow below a toolbar once we start scrolling and making that drop
 * shadow go away once you have scrolled back to the top.
 */ export function useCanvasScroll(args) {
    const { scrollingElementRef , onScrollTop , onScrollThresholdExceeded , scrollTopThreshold  } = args;
    // When the page loads, there is no drop shadow. The first scroll introduces the drop shadow.
    React.useEffect(()=>{
        const scrollingElement = scrollingElementRef.current;
        if (!scrollingElement) {
            return undefined;
        }
        // Tracks what we last reported to the consumer of the hook as the scroll position. We assume we start with the content scrolled to the top
        let lastReportedThresholdExceeded = false;
        // Tracks if a rAF has been queued to handle a scroll event. Prevents negative perf implications of multiple handlers running per animation frame
        let isRafScheduled = false;
        const scrollHandler = (ev)=>{
            if (!isRafScheduled) {
                // Checking scrollTop is expensive, only do it when the browser is about to paint anyway
                requestAnimationFrame(()=>{
                    if (ev.target && ev.target instanceof Element) {
                        const isPastScrollTopThreshold = ev.target.scrollTop > scrollTopThreshold;
                        if (isPastScrollTopThreshold) {
                            // Only invoke the handler the scroll threshold was crossed on this scroll
                            if (!lastReportedThresholdExceeded) {
                                onScrollThresholdExceeded();
                            }
                        } else {
                            // Only invoke the handler the scroll threshold was crossed on this scroll
                            if (lastReportedThresholdExceeded) {
                                onScrollTop();
                            }
                        }
                        lastReportedThresholdExceeded = isPastScrollTopThreshold;
                    }
                    isRafScheduled = false;
                });
            }
            isRafScheduled = true;
        };
        scrollingElement.addEventListener('scroll', scrollHandler, {
            capture: true,
            passive: true
        });
        return ()=>{
            scrollingElement.removeEventListener('scroll', scrollHandler, {
                capture: true
            });
        };
    // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
